import { DESKTOP_BREAKPOINT } from '../default-modules/default-variables';

const CONTAINER_ID = 'wl-checkout-products';
const PRODUCTS_URL = '/sv/meny/Produkter/Outlet';
const MAX_PRODUCTS = 12;

// SlidesToShow per skärmbredd. 1201+ visar 4 enligt designens skyltningsregler.
const SLIDER_LARGE_BREAKPOINT = 1200;
const SLIDES_DESKTOP = 4;
const SLIDES_LARGE = 3;
const SLIDES_SMALL = 2;

let hooksBound = false;

// Injicerar en cross-sell-container mellan varukorgen och summeringen i kassan.
// Produktlistan hämtas via AJAX och visas som klickbara kort i en slick-slider.
export function checkoutPage() {
  if (!document.body.classList.contains('page-Checkout')) return;

  injectCrossSellContainer();
  bindUpdateHooks();
}

function injectCrossSellContainer() {
  const cart = document.getElementById('CheckoutCart');
  if (!cart) return;

  // Idempotent: containern byggs bara en gång.
  if (document.getElementById(CONTAINER_ID)) return;

  const section = buildContainerSkeleton();
  cart.insertAdjacentElement('afterend', section);

  loadProducts(section);
}

function buildContainerSkeleton() {
  const heading = getHeading();

  const section = document.createElement('section');
  section.id = CONTAINER_ID;
  section.className = 'wl-checkout-products wl-script-added';
  section.setAttribute('aria-label', heading);

  const title = document.createElement('h2');
  title.className = 'wl-checkout-products__heading';
  title.textContent = heading;

  const list = document.createElement('div');
  list.className = 'wl-checkout-products__list';

  section.appendChild(title);
  section.appendChild(list);

  return section;
}

function getHeading() {
  if (document.querySelector('html[lang="en"]')) {
    return 'Other customers also bought';
  }

  return 'Andra kunder köpte också';
}

async function loadProducts(section) {
  const list = section.querySelector('.wl-checkout-products__list');
  if (!list) return;

  try {
    const response = await fetch(PRODUCTS_URL, { credentials: 'include' });
    if (!response.ok) throw new Error(`Kunde inte hämta produktlistan: ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const products = parseProducts(doc);

    if (!products.length) {
      section.classList.add('wl-checkout-products--empty');
      return;
    }

    const fragment = document.createDocumentFragment();
    products.forEach((product) => fragment.appendChild(buildProductCard(product)));
    list.appendChild(fragment);

    section.classList.add('wl-checkout-products--ready');
    initSlider(list);
  } catch (_error) {
    section.classList.add('wl-checkout-products--error');
  }
}

function parseProducts(doc) {
  const containers = Array.from(doc.querySelectorAll('.prodlist .productContainer')).slice(0, MAX_PRODUCTS);

  return containers.map(extractProductData).filter((product) => product !== null);
}

function extractProductData(container) {
  const link = container.querySelector('a.itemnamelink') || container.querySelector('.product-name a');
  const name = link?.textContent.trim();
  const href = link?.getAttribute('href');

  if (!name || !href) return null;

  const image = container.querySelector('img.product-image, img.ArtImg');

  return {
    name,
    href,
    imageSrc: image?.getAttribute('data-src') || image?.getAttribute('src') || '',
    imageAlt: image?.getAttribute('alt') || name,
    price: extractPrice(container)
  };
}

function extractPrice(container) {
  const saleValue = container.querySelector('.price-wrapper .price.sale .value')?.textContent.trim() || '';
  const baseValue = container.querySelector('.price-wrapper .price:not(.sale) .value')?.textContent.trim() || '';

  const current = saleValue || baseValue;
  const original = saleValue ? baseValue : '';

  return { current, original, isSale: Boolean(saleValue && original) };
}

function buildProductCard(product) {
  const card = document.createElement('a');
  card.className = 'wl-checkout-products__card wl-script-added';
  card.href = product.href;

  card.appendChild(buildCardImage(product));
  card.appendChild(buildCardBody(product));

  return card;
}

function buildCardImage(product) {
  const figure = document.createElement('div');
  figure.className = 'wl-checkout-products__image';

  if (!product.imageSrc) return figure;

  const img = document.createElement('img');
  img.src = product.imageSrc;
  img.alt = product.imageAlt;
  img.loading = 'lazy';
  figure.appendChild(img);

  return figure;
}

function buildCardBody(product) {
  const body = document.createElement('div');
  body.className = 'wl-checkout-products__body';

  const name = document.createElement('span');
  name.className = 'wl-checkout-products__name';
  name.textContent = product.name;

  body.appendChild(name);
  body.appendChild(buildCardPrice(product.price));

  return body;
}

function buildCardPrice(price) {
  const wrapper = document.createElement('div');
  wrapper.className = 'wl-checkout-products__price';

  if (!price.current) return wrapper;

  if (price.isSale) {
    wrapper.classList.add('wl-checkout-products__price--sale');

    const original = document.createElement('span');
    original.className = 'wl-checkout-products__price-original';
    original.textContent = formatPrice(price.original);
    wrapper.appendChild(original);
  }

  const current = document.createElement('span');
  current.className = 'wl-checkout-products__price-current';
  current.textContent = formatPrice(price.current);
  wrapper.appendChild(current);

  return wrapper;
}

function formatPrice(value) {
  // Plattformen levererar t.ex. "495.00". Visa heltal med svensk valutasuffix.
  const numeric = Number.parseFloat(value);
  const rounded = Number.isFinite(numeric) ? Math.round(numeric).toString() : value;

  return `${rounded} kr`;
}

function initSlider(list) {
  if (typeof window.$ !== 'function' || !window.$.fn?.slick) return;

  const $list = window.$(list);
  if ($list.hasClass('slick-initialized')) return;

  $list.slick({
    infinite: false,
    speed: 300,
    slidesToShow: SLIDES_DESKTOP,
    slidesToScroll: 1,
    arrows: true,
    dots: false,
    responsive: [
      { breakpoint: SLIDER_LARGE_BREAKPOINT, settings: { slidesToShow: SLIDES_LARGE, slidesToScroll: 1 } },
      { breakpoint: DESKTOP_BREAKPOINT, settings: { slidesToShow: SLIDES_SMALL, slidesToScroll: 1 } }
    ]
  });
}

// Kassan kan laddas om delvis via ASP.NET UpdatePanel. Vi bygger om containern
// enbart om den försvunnit, så att inget dubbleras. Ingen polling enligt e-line-standard.
function bindUpdateHooks() {
  if (hooksBound) return;
  hooksBound = true;

  const prm = window.Sys?.WebForms?.PageRequestManager?.getInstance?.();
  if (!prm) return;

  prm.add_pageLoaded(() => {
    if (!document.getElementById(CONTAINER_ID)) {
      injectCrossSellContainer();
    }
  });
}
